import { Fragment, FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { PendingDotsSkeleton } from "../../../../components/Shared/LoadingSkeletons/PendingDotsSkeleton/PendingDotsSkeleton";
import { translationKeys } from "../../../../models/app-translation-keys";
import { useStore } from "../../../../stores/StoreContext";
import { Exchange } from "../ExistingChat/Exchange";

import "./ActiveThread.scss";

import { ChatThreadExtendedModel } from "../../../../models/interfaces";

const RawActiveThread: FunctionComponent = () => {
  const { t } = useTranslation();
  const { threadStore } = useStore();
  const lastElementRef = useRef<HTMLDivElement>(null);
  const scrollableContainerRef = useRef<HTMLDivElement>(null);

  const [activeThread, setActiveThread] = useState<ChatThreadExtendedModel | undefined>();

  useEffect(() => {
    // When a new exchange is added, we want to scroll to the end of the thread smoothly
    if (threadStore.activeThread && threadStore.activeThread?.exchanges?.length !== activeThread?.exchanges?.length) {
      setActiveThread(threadStore.activeThread);
      scrollSmoothToEnd();
    }
  }, [threadStore.activeThread?.exchanges?.length]);

  useEffect(() => {
    // When the active thread changes, we want to scroll to the end of the thread without animation
    if (threadStore.activeThread && threadStore.activeThread?.id !== activeThread?.id) {
      setActiveThread(threadStore.activeThread);
      scrollFastToEnd();
    }
  }, [threadStore.activeThread?.id]);

  useEffect(() => {
    // When a new pending exchange is added, we want to scroll to the end of the thread smoothly
    if (threadStore.activeThread?.pendingPrompt) {
      scrollSmoothToEnd();
    }
  }, [threadStore.activeThread?.pendingPrompt]);

  const scrollSmoothToEnd = () => {
    setTimeout(() => {
      lastElementRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 100);
  };

  const scrollFastToEnd = () => {
    lastElementRef?.current?.scrollIntoView({
      behavior: "instant",
      block: "end",
    });
  };

  const mappedExchangesMemo = useMemo(
    () => (
      <Fragment>
        <div className="thread-beginning">{t(translationKeys.threadRow.threadBeginning)}</div>

        <div>
          {threadStore.activeThread?.exchanges?.slice().map(exchange => (
            <div key={exchange.id} className="thread-exchange">
              {/* User */}
              <Exchange exchange={exchange} content={exchange.prompt} isAi={false} />

              {/* AI */}
              <Exchange
                exchange={exchange}
                content={exchange.answer!}
                isAi={true}
                sources={exchange.sources}
                exchangeId={exchange.id}
                error={exchange.error}
                groundingServiceError={exchange.groundingServiceError}
              />
            </div>
          ))}
        </div>
      </Fragment>
    ),
    [threadStore.activeThread],
  );

  return (
    <div className="thread-component" ref={scrollableContainerRef}>
      <div className="space-filler" />

      <div>{!!threadStore.activeThread?.exchanges?.length && mappedExchangesMemo}</div>

      {threadStore.activeThread?.pendingPrompt && (
        <div className="exchange-sent-wrapper">
          <div className="pending-exchange-question">
            <Exchange content={threadStore.activeThread.pendingPrompt} isAi={false} />
          </div>

          <div className="exchange-progress-skeleton">
            <PendingDotsSkeleton translationKey={translationKeys.feature.chatbot.actions.loadingAIAnswer} />
          </div>
        </div>
      )}

      <div ref={lastElementRef} className="last-element-to-scroll-into" />
    </div>
  );
};

export const ActiveThread = observer(RawActiveThread);
